import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { first } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PayrollFrequency } from '../../../models/payroll-info';
import {
  CompanyLocationConfig,
  PayrollCalendarSetup
} from '../../../models/company-location-config';
import {
  MasterPayrollObject,
  PayDateInfo,
  PayrollStatuses,
  PayTypes,
  Schedule
} from '../../../models/payroll';
import { AdminService } from '../../../services/admin.service';
import { AppTextService } from '../../../services/app-text.service';
import { AuditService } from '../../../services/audit.service';
import { AuthService } from '../../../services/auth.service';
import { CompaniesService } from '../../../services/companies.service';
import { ModalService } from '../../../services/modal.service';
import { PayrollService } from '../../../services/payroll.service';
import { ModalStates } from '../../../models/modal';
import { PayrollManagement } from '../../../tools/payroll-management';
import { AutoPayrollSkipModal } from '../auto-payroll-skip-modal/auto-payroll-skip-modal.component';
import { Observable, of } from 'rxjs';

@Component({
  selector: 'auto-payroll-home',
  templateUrl: './auto-payroll-home.component.html',
  styleUrls: ['./auto-payroll-home.component.css'],
  standalone: false
})
export class AutoPayrollHomeComponent implements OnInit {
  beforeYouBeginText: string;
  calendar: PayrollCalendarSetup;
  clientCode: string;
  disableCtrls = false;
  payDate: Date;
  payGrid: MasterPayrollObject;
  periodBegin: Date;
  periodEnd: Date;
  location: CompanyLocationConfig;
  currentUserName: string;
  schedule: string;
  frequency: string;

  constructor(
    private adminService: AdminService,
    private appTextService: AppTextService,
    private auditService: AuditService,
    private authService: AuthService,
    private companyService: CompaniesService,
    private ngModalService: NgbModal,
    private modalService: ModalService,
    private payrollService: PayrollService,
    public payrollMgmt: PayrollManagement,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.appTextService.getAppText('PayrollBeforeYouBegin').subscribe(text => {
      this.beforeYouBeginText = text;
    });
  }

  ngOnInit() {
    this.clientCode = this.route.snapshot.paramMap.get('client_code');
    this.currentUserName = this.authService.getCurrentUserName();

    // check to make sure this is a valid auto payroll request
    this.companyService.getLocations(this.clientCode).subscribe(locationConfigs => {
      // if client is NOT setup to auto pay - bounce them to normal payroll
      if (!this.payrollMgmt.isClientSetupForAutoPayroll(locationConfigs)) {
        this.router.navigate([`/client/${this.clientCode}/payroll`]);
      } else {
        // US 25441 - allow regular payroll when auto payroll has been skipped
        const location = locationConfigs[0];
        this.payrollMgmt
          .isAutoPayrollSkipped(this.clientCode, location.Id, location.Calendars[0].CalendarId)
          .subscribe(skipped => {
            if (skipped) {
              // automatic payroll has been skipped, redirect to regular payroll start
              this.router.navigate([`/client/${this.clientCode}/payroll`]);
            } else {
              // automatic payroll has not been skipped, continue with page load
              this._fetchCompanyPayrollData(locationConfigs);
            }
          });
      }
    });
  }

  // #region PUBLIC METHODS

  confirmSkipAutoPay(): Observable<any> {
    // if paygrid already exists and is not skipped
    if (this.payGrid && !this.payGrid.IsAutomaticSkipped) {
      this.payGrid.IsAutomaticSkipped = true;
      this.payGrid.LastEditOn = new Date();
      this.payGrid.LastEditBy = this.authService.getCurrentUserName();
      return this.payrollService.updatePayGrid(this.clientCode, this.payGrid.Id, this.payGrid);
    } else if (!this.payGrid) {
      const cal = this.location.Calendars[0];
      this.payGrid = new MasterPayrollObject(
        this.clientCode,
        [
          new Schedule(
            cal.CalendarId,
            0,
            this.periodEnd,
            PayrollFrequency[this.frequency],
            cal.CalendarName,
            this.periodBegin,
            this.periodEnd,
            this.payDate
          )
        ],
        this.location.Id,
        this.payDate,
        true,
        false,
        PayrollStatuses.Started,
        this.currentUserName,
        new Date()
      );
      return this.payrollService.createPayGrid(this.clientCode, this.payGrid);
    }

    // Paygrid has already been skipped, we should never land here, but this is a no-op
    return of();
  }

  resumeWorksheet() {
    if (!this.payGrid) {
      return;
    }

    this.router.navigate([`/client/${this.clientCode}/autopayroll/${this.payGrid.Id}`]);
  }

  startWorksheet() {
    // get initialized pay grid. then create the grid in the database and navigate to worksheet
    const payDateInfo = new PayDateInfo();
    payDateInfo.ClientId = this.clientCode;
    payDateInfo.CalendarId = this.location.Calendars.map(x => x.CalendarId)[0];
    payDateInfo.LocationId = this.location.Id;
    payDateInfo.PeriodBegin = this.periodBegin;
    payDateInfo.PeriodEnd = this.periodEnd;
    payDateInfo.TargetPayDate = this.payDate;

    this.payrollMgmt.createAutoPayWorksheet(payDateInfo).subscribe(payroll => {
      // audit
      this.auditService.audit(`auto-payroll-generated`, {
        ClientCode: this.clientCode,
        PayrollId: payroll.Id,
        PeriodEnd: this.payrollMgmt.periodEndDisplay(payroll),
        PayDate: payroll.PayDate,
        LocationId: payroll.LocationId
      });

      this.router.navigate([`/client/${this.clientCode}/autopayroll/${payroll.Id}`]);
    });
  }

  skipAutomaticPay() {
    var modalRef = this.ngModalService.open(AutoPayrollSkipModal, {
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
      windowClass: 'centered'
    });

    const c = modalRef.componentInstance as AutoPayrollSkipModal;

    c.payDate = this.payDate;
    c.periodEndDate = this.periodEnd;

    c.onSkip = () => {
      this.confirmSkipAutoPay().subscribe(() => {
        modalRef.close();
        this.auditPayrollSkipped();
        this.router.navigate([`/client/${this.clientCode}/payroll-landing`]);
      });
    };

    c.onSkipAndNavigate = () => {
      this.confirmSkipAutoPay().subscribe(() => {
        modalRef.close();
        this.auditPayrollSkipped();
        this.router.navigate([`/client/${this.clientCode}/payroll`]);
      });
    };

    c.onCancel = () => {
      modalRef.close();
    };
  }

  private auditPayrollSkipped() {
    this.auditService.auditPayroll(`auto-payroll-skipped`, {
      ClientCode: this.clientCode,
      PayrollId: this.payGrid.Id,
      PayDate: this.payDate,
      LocationId: this.location.Id,
      PayrollType: PayTypes.Regular,
      Calendars: [
        {
          CalendarId: this.calendar?.CalendarId,
          Frequency: this.calendar?.Frequency,
          PeriodBegin: this.periodBegin,
          PeriodEnd: this.periodEnd,
          ScheduledPayDate: this.payDate,
          ScheduledPeriodEnd: this.periodEnd,
          ScheduleName: this.calendar?.CalendarName,
          SeqNo: this.calendar?.CalendarId
        }
      ]
    });
  }

  // #endregion

  // #region PRIVATE METHODS

  private _fetchCompanyPayrollData(locations: CompanyLocationConfig[]) {
    if (!locations || locations.length === 0) {
      this.modalService.showAlertModal('Failed to fetch company locations', ModalStates.Error);
    }

    // find first location that has a calendar setup for auto payroll
    if (locations && locations.length) {
      locations.forEach(location => {
        location.Calendars.forEach(calendar => {
          if (calendar.IsPayrollAuto && !this.location && !this.calendar) {
            this.location = location;
            this.calendar = calendar;
          }
        });
      });
    }

    this.payrollService
      .getNextPayDate(this.clientCode, this.location.Id, this.calendar.CalendarId)
      .subscribe(pay => {
        this.schedule = pay.CalendarName;
        this.frequency = PayrollFrequency[pay.Frequency];
        this.payDate = pay.TargetPayDate;
        this.periodBegin = pay.PeriodBegin;
        this.periodEnd = pay.PeriodEnd;

        // with pay date and period end, check if a pay grid already exists
        this.payrollMgmt
          .findMatchingWorksheet(
            this.clientCode,
            this.location.Id,
            this.location.Calendars.map(x => x.CalendarId)[0],
            this.payDate
          )
          .subscribe(grid => {
            this.payGrid = grid;
            this._initializePage();
          });
      });
  }

  // FIXME: looks like we can remove this function and just move its single
  // operation into _fetchCompanyPayrollData?   --nk 2021-07-30
  private _initializePage() {
    this.disableCtrls = this.payGrid && this.payGrid.IsAutomaticSkipped;
  }

  // #endregion
}
